import { Injectable } from '@angular/core';
import { AttachmentModalData, AttachmentModalEntityData } from '../../Attachment/attachment.modal.model';
import { CommentModalData } from '../../Comments/comments.service';
import { TaskService } from '../../Task/task.service.upgrade';
import { AnnualDetailAssessment, AnnualDetailYear } from '../Annual-Year/annual-year.model';
import { Appeal, AppealLevel, AppealStatus, AppealStatusID } from './appeal.model';
import { BulkOperationResult } from '../../Common/Models/bulk-operation-result.model';
import { lastValueFrom } from 'rxjs';
import { BaseRepository } from 'src/app/Common/Data/base.repository';
import { SaveHandlerOptions } from '../annual-details-navigation.service';
import { HttpClient } from '@angular/common/http';

declare const _: any;

// TODO: Handle global consts in Angular 2+
const APPEAL_ENTITY_TYPE_ID = 8;

export class AppealViewDTO {
    annualDetailYear: AnnualDetailYear;
    appeals: Appeal[];
    stateID: number;
    savingsCalculationDetails: Core.AppealSavingsCalculationDetails;
}

@Injectable()
export class AppealService extends BaseRepository {
    constructor(
        httpClient: HttpClient,
        private taskService: TaskService,
    ) {
        super(httpClient);
    }

    //******************** API Calls ********************
    getStateAppealLevels(stateID: number): Promise<AppealLevel[]> {
        return lastValueFrom(this.httpGet(`/api/appeallevel/state/${stateID}`));
    }

    getAppealsForAnnualYear(annualYearID: number): Promise<Appeal[]> {
        return lastValueFrom(this.httpGet(`/api/appeal/annualyear/${annualYearID}`));
    }

    getAppealView(appealID: number): Promise<AppealViewDTO> {
        return lastValueFrom(this.httpGet(`api/annualassessmentview/appeal/${appealID}`)).then((result: any) => {
            // The server calls the annualDetailYear "annualYearView", so we'll change the name here
            result.annualDetailYear = result.annualYearView;
            delete result.annualYearView;
            return result as AppealViewDTO;
        });
    }

    saveAppeal(appeal: Appeal, options?: SaveHandlerOptions): Promise<Appeal> {
        return lastValueFrom(this.httpPut('/api/appeal', appeal));
    }

    createAppeal(appeal: Appeal, addLitigationReferralTask?: boolean): Promise<Appeal> {
        // Note that many properties of appeal will be ignored; as of this comment, we're only
        // saving the appeal level ID and assessment ID, leaving everything else at defaults

        // Also, some properties that we don't have selectable on the UI are required by the UI
        if (!appeal.appealStatusID) {
            appeal.appealStatusID = null; //AppealStatusID.Open;  it's nullable now, see WK-2102
        }

        const isLitigationReferral =
            addLitigationReferralTask !== undefined
                ? { params: { addLitigationReferralTask: addLitigationReferralTask } }
                : undefined;

        // Note that the server normally fills in a property called "appealLevel" on appeal objects,
        // but it is not returned on the create API call. The Appeal model on the UI does not have
        // an appealLevel property, so it shouldn't create any problems, but if we ever add the
        // appealLevel property to the UI model and lean on it, we should correct that issue here.
        return lastValueFrom(this.httpPost('/api/appeal', appeal, isLitigationReferral));
    }

    deleteAppeal(appealID: number): Promise<void> {
        return lastValueFrom(this.httpDelete(`/api/appeal/${appealID}`));
    }

    //****************** End API Calls ******************

    loadTaskSummary(viewModel: AppealViewModel) {
        viewModel.currentTaskSummary = null;
        viewModel.loadingTaskSummary = true;
        const appealID = viewModel.currentAppeal.appealID;
        this.taskService.getTaskSummaryByEntity(appealID, APPEAL_ENTITY_TYPE_ID).then((taskSummary) => {
            // If we've navigated to another appeal by the time this happens, don't pay attention to the result
            if (appealID === viewModel.currentAppeal.appealID) {
                viewModel.loadingTaskSummary = false;
                viewModel.currentTaskSummary = taskSummary[0];
            }
        });
    }

    setCurrentAppeal(viewModel: AppealViewModel, appealID: number): void {
        const appealIndex = _.findIndex(viewModel.model, (appeal) => {
            return appeal.appealID === appealID;
        });

        if (appealIndex >= 0) {
            this.setAppealIndex(viewModel, appealIndex);
        }
    }

    setAppealIndex(viewModel: AppealViewModel, appealIndex: number, force: boolean = false): void {
        if (force || viewModel.currentAppealIndex !== appealIndex) {
            viewModel.currentAppealIndex = appealIndex;
            viewModel.currentAppeal = viewModel.model[appealIndex];
            viewModel.resetEdit();
            viewModel.updateModalData();
            this.loadTaskSummary(viewModel);
        }
    }

    setAppealByAssessmentID(viewModel: AppealViewModel, assessmentID: number): void {
        const appealIndex = _.findIndex(viewModel.model, (appeal: Appeal) => {
            return appeal.annualAssessmentID === assessmentID;
        });

        if (appealIndex >= 0) {
            this.setAppealIndex(viewModel, appealIndex);
        }
    }

    addNewAppealToViewModel(viewModel: AppealViewModel, appeal: Appeal): Promise<Appeal> {
        const originalModel = viewModel.model || [];
        viewModel.model = [];
        // HACK: For whatever reason, ngx-bootstrap's tab control didn't handle this
        // correctly without the setTimeout; this gives it time to "breathe" after resetting
        // the list
        return new Promise<Appeal>((resolve) => {
            setTimeout(() => {
                originalModel.unshift(appeal);
                viewModel.model = originalModel;
                this.setAppealIndex(viewModel, 0, true);
                resolve(appeal);
            }, 1);
        });
    }

    saveAppealFromViewModel(viewModel: AppealViewModel, options?: SaveHandlerOptions): Promise<Appeal> {
        return this.saveAppeal(viewModel.currentAppeal, options);
    }

    cancelAppealEdit(viewModel: AppealViewModel): void {
        viewModel.cancelEdit();
    }

    toggleAppealEdit(viewModel: AppealViewModel, editMode: boolean): void {
        if (editMode) {
            viewModel.beginEdit();
        }
    }

    async setSavingsCalculationDetails(appeal: Appeal): Promise<Core.AppealSavingsCalculationDetails> {
        if (appeal.appealStatusID !== Core.AppealStatusEnum.Reduction && !appeal.anticipatedFMV) {
            return Promise.resolve({ appealId: appeal.appealID } as Core.AppealSavingsCalculationDetails);
        } else {
            var payload = {
                annualAssessmentId: appeal.annualAssessmentID,
                outcomeRevisionId: appeal.outcomeAnnualAssessmentID || 0,
                anticipatedFMV: appeal.anticipatedFMV,
            };

            return lastValueFrom(this.httpPost(`/api/appeal/${appeal.appealID}/calculateSavings`, payload));
        }
    }

    async getSavingsCalculationDetailsInfoIconData(appeal: Appeal): Promise<Core.AppealSavingsCalculationDetails> {
        var payload = {
            annualAssessmentId: appeal.annualAssessmentID,
            outcomeRevisionId: appeal.outcomeAnnualAssessmentID || 0,
            anticipatedFMV: appeal.anticipatedFMV,
        };

        return lastValueFrom(this.httpPost(`/api/appeal/${appeal.appealID}/calculateSavings`, payload));
    }

    // Whenever the user manually updates the "Savings" field, the "savingsOverriden" flag on the
    // appeal is set. For the specified appeal, this function computes the savings if they have
    // not been overriden.
    setSavings(
        appeal: Appeal,
        annualYearModel: AnnualDetailYear,
        appealSavingsCalculationDetails: Core.AppealSavingsCalculationDetails,
    ): Promise<void> {
        if (appeal.savingsOverridden) {
            return Promise.resolve();
        }

        if (
            appeal.appealStatusID !== Core.AppealStatusEnum.Reduction &&
            !appeal.anticipatedFMV &&
            appeal.anticipatedFMV !== 0
        ) {
            const outcomeRevision = <AnnualDetailAssessment>(
                _.find(annualYearModel.annualGridDetails, { annualAssessmentID: appeal.outcomeAnnualAssessmentID })
            );
            appeal.savings = outcomeRevision ? 0 : null;
            return Promise.resolve();
        } else {
            return lastValueFrom(
                this.httpPost(`/api/appeal/${appeal.appealID}/calculateSavings`, {
                    annualAssessmentId: appeal.annualAssessmentID,
                    outcomeRevisionId: appeal.outcomeAnnualAssessmentID || 0,
                    anticipatedFMV: appeal.anticipatedFMV,
                }),
            ).then((calculateSavingsInfo: Core.AppealSavingsCalculationDetails) => {
                appeal.savings = calculateSavingsInfo.totalSavings;
                appealSavingsCalculationDetails = calculateSavingsInfo;
            });
        }

        return Promise.resolve();
    }

    isClosed(appeal: Appeal): boolean {
        if (!appeal.appealStatusID) {
            return false;
        }

        const status = AppealStatus.getByID(appeal.appealStatusID);
        return status.isClosedStatus;
    }

    addPrepareApplicationTasks(taskIds: number[]): Promise<BulkOperationResult[]> {
        return lastValueFrom(this.httpPut('api/tasks/addprepareapplicationtasks', taskIds));
    }

    getAppealViewModelByYear(
        annualYearModel: AnnualDetailYear,
        parcelID: number,
        parcelAcctNum: string,
        stateID: number,
        defaultAppealID?: number,
    ): Promise<AppealViewModel> {
        return Promise.all([
            this.getStateAppealLevels(stateID),
            this.getAppealsForAnnualYear(annualYearModel.annualYearID),
        ]).then((result) => {
            const viewModel = this.buildAppealViewModel(result[1], result[0], annualYearModel, parcelID, parcelAcctNum);
            viewModel.defaultAppealID = defaultAppealID;

            return viewModel;
        });
    }

    getAppealViewModelByAppealID(appealID: number): Promise<AppealViewModel> {
        return this.getAppealView(appealID).then((appealView) => {
            return this.getStateAppealLevels(appealView.stateID).then((appealLevels) => {
                const viewModel = this.buildAppealViewModel(
                    appealView.appeals,
                    appealLevels,
                    appealView.annualDetailYear,
                    null,
                    null,
                );
                viewModel.defaultAppealID = appealID;
                this.setCurrentAppeal(viewModel, appealID);
                viewModel.showTabs = false;

                return viewModel;
            });
        });
    }

    private buildAppealViewModel(
        appeals: Appeal[],
        appealLevels: AppealLevel[],
        annualYearModel: AnnualDetailYear,
        parcelID: number,
        parcelAcctNum: string,
    ): AppealViewModel {
        const viewModel = new AppealViewModel();
        viewModel.stateAppealLevels = appealLevels;
        viewModel.annualYearModel = annualYearModel;
        viewModel.model = appeals;
        viewModel.showTabs = true;
        viewModel.parcelID = parcelID;
        viewModel.parcelAcctNum = parcelAcctNum;

        if (viewModel.model.length < 1) {
            viewModel.currentAppealIndex = -1;
            viewModel.currentAppeal = null;
        } else {
            let latestAppealIdx = 0;

            const latestAppeal = _.maxBy(appeals, (x) => new Date(x.addDate));
            if (latestAppeal) {
                latestAppealIdx = _.findIndex(appeals, { appealID: latestAppeal.appealID });
            }

            this.setAppealIndex(viewModel, latestAppealIdx);

            viewModel.appealSavingsCalculationDetails = {
                appealId: viewModel.currentAppeal.appealID,
                parcelSavingsCalculations: [],
                totalSavings: 0,
            } as Core.AppealSavingsCalculationDetails;
        }

        return viewModel;
    }
}

export class AppealViewModel {
    constructor() {
        // This will be set by the appeal component when loaded
        this.hasWritePermission = false;

        this.submitEvidenceValid = true;
        this.informalHearingValid = true;
        this.hearingValid = true;
        this.resetDateTimeHandler = null;
    }

    model: Appeal[];
    annualYearModel: AnnualDetailYear;
    stateAppealLevels: AppealLevel[];
    loadingTaskSummary: boolean;
    currentTaskSummary: any;
    currentAppealIndex: number;
    currentAppeal: Appeal;
    parcelID: number;
    parcelAcctNum: string;
    showTabs: boolean;
    defaultAppealID: number;
    commentModalData: CommentModalData;
    attachmentModalData: AttachmentModalData;
    hasWritePermission: boolean;
    submitEvidenceValid: boolean;
    informalHearingValid: boolean;
    hearingValid: boolean;
    resetDateTimeHandler: () => void;
    private preEditModelBackup: Appeal;
    appealSavingsCalculationDetails: Core.AppealSavingsCalculationDetails;

    // Usually, the backup object used for cancelling will be set whenever a navigation event happens,
    // and the navigation service informs us we need to handle it in the beginEdit handler. However,
    // within the appeal control, there are some circumstances where the backup must be created again.
    // At the moment, those circumstances are:
    // 1. Switching tabs
    // 2. An automatic save occurring before opening the task modal
    resetEdit(force: boolean = false): void {
        if (force || (this.currentAppeal &&
            this.preEditModelBackup &&
            this.currentAppeal.appealID !== this.preEditModelBackup.appealID)) {
            this.preEditModelBackup = _.cloneDeep(this.currentAppeal) as Appeal;
            console.log(['Creating preEditModelBackup', this.preEditModelBackup]);
            if (this.resetDateTimeHandler) {
                this.resetDateTimeHandler();
            }
        }
    }

    beginEdit(): void {
        if (this.currentAppeal) {
            this.preEditModelBackup = _.cloneDeep(this.currentAppeal) as Appeal;
            if (this.resetDateTimeHandler) {
                this.resetDateTimeHandler();
            }
        } else {
            this.preEditModelBackup = undefined;
            if (this.resetDateTimeHandler) {
                this.resetDateTimeHandler();
            }
        }
    }

    cancelEdit(): void {
        if (this.currentAppeal.appealID === this.preEditModelBackup.appealID) {
            _.assign(this.currentAppeal, this.preEditModelBackup);
            if (this.resetDateTimeHandler) {
                this.resetDateTimeHandler();
            }
        } else {
            throw new Error(`Sanity check failed; attempted to restore backup with different appeal ID than current. Current appeal ID: ${
                this.currentAppeal.appealID}, backup appeal ID: + ${this.preEditModelBackup.appealID}`);
        }
    }

    assignFromExistingAppeal(appeal: Appeal): void {
        if (!this.currentAppeal) {
            throw new Error('Invalid attempt to load appeal from existing data when no current appeal is set');
        }

        if (this.currentAppeal.appealID !== appeal.appealID) {
            throw new Error(`Invalid attempt to appeal appeal with appealID ${appeal.appealID
            } into appeal with appealID ${this.currentAppeal.appealID}`);
        }

        _.assign(this.currentAppeal, appeal);
        if (this.resetDateTimeHandler) {
            this.resetDateTimeHandler();
        }
    }

    appealLevelAbbrDisplay(appealLevelID: number): string {
        const appealLevel = _.find(this.stateAppealLevels, (x) => {
            return x.appealLevelID === appealLevelID;
        }) as AppealLevel;

        return appealLevel === undefined ? '' : appealLevel.abbr;
    }

    appealLevelLitigationDisplay(appealLevelId: number): string {
        const appealLevel = _.find(this.stateAppealLevels, (x) => {
            return x.appealLevelID === appealLevelId;
        }) as AppealLevel;

        return appealLevel === undefined
            ? ''
            : appealLevel.litigation ? 'Yes' : 'No';
    }

    setParcelDetails(parcelID: number, parcelAcctNum: string): void {
        this.parcelID = parcelID;
        this.parcelAcctNum = parcelAcctNum;
        this.updateModalData();
    }

    updateModalData(): void {
        const appeal = this.currentAppeal;
        if (appeal) {
            const appealLevel = this.appealLevelAbbrDisplay(appeal.appealLevelID);
            const description = `${this.annualYearModel.annualYear1} - ${appealLevel}`;

            // comment data
            this.commentModalData = new CommentModalData();
            this.commentModalData.entityID = appeal.appealID;
            this.commentModalData.entityTypeID = APPEAL_ENTITY_TYPE_ID;
            this.commentModalData.canEdit = this.hasWritePermission;
            this.commentModalData.parcelID = this.parcelID;
            this.commentModalData.parcelAcctNum = this.parcelAcctNum;
            this.commentModalData.description = description;
            this.commentModalData.year = this.annualYearModel.annualYear1.toString();

            // attachment data
            this.attachmentModalData = new AttachmentModalData();
            this.attachmentModalData.belowParcelEntity = new AttachmentModalEntityData();
            this.attachmentModalData.belowParcelEntity.id = appeal.appealID;
            this.attachmentModalData.belowParcelEntity.typeId = APPEAL_ENTITY_TYPE_ID;
            this.attachmentModalData.belowParcelEntity.name = appealLevel;
            this.attachmentModalData.entityType = 'Appeal';
            this.attachmentModalData.parentId = this.parcelID;
            this.attachmentModalData.parentType = 'Parcel';
            this.attachmentModalData.entityName = this.parcelAcctNum;
            this.attachmentModalData.entityDescription = description;
            this.attachmentModalData.year = this.annualYearModel.annualYear1;
        }
    }

    validate(callback: (boolean, string) => void): void {
        const invalidTimes = [];

        // For now I believe this is only possible by entering a date but not a time
        // TODO: Write this in a less crappy way
        if (!this.submitEvidenceValid) {
            invalidTimes.push('Submit Evidence');
        }
        if (!this.informalHearingValid) {
            invalidTimes.push('Informal Hearing');
        }
        if (!this.hearingValid) {
            invalidTimes.push('Formal Hearing');
        }

        let validationMessage = '';

        switch (invalidTimes.length) {
            case 1:
                validationMessage = ' date/time not valid';
                break;
            case 2:
                validationMessage = `${invalidTimes[0]} and ${invalidTimes[1]} date/time not valid`;
                break;
            case 3:
                validationMessage = `${invalidTimes[0]}, ${invalidTimes[1]}, and ${invalidTimes[2]} date/time not valid`;
                break;
        }

        const appealStatusID = this.currentAppeal.appealStatusID;
        if ((appealStatusID === AppealStatusID.Reduction || (appealStatusID === AppealStatusID.Closed && this.currentAppeal.savings > 0)) && !this.currentAppeal.savingsDate) {
            validationMessage = `Savings Date is required${validationMessage === '' ? '' : (`; ${validationMessage}`)}`;
        } else if (this.currentAppeal.appealStatusID === AppealStatusID.Null && this.currentAppeal.savingsDate) {
            validationMessage = `Savings Date is not valid unless the appeal is closed${validationMessage === '' ? '' : (`; ${validationMessage}`)}`;
        }

        if (validationMessage !== '') {
            callback(false, validationMessage);
        } else {
            callback(true, '');
        }
    }
}
